/**
 * Pure week-aggregation model for the liquidity forecast chart.
 *
 * Maps payment-calendar day buckets into ISO-week (Mon..Sun) buckets:
 *
 *   opening   = openingBalancePln of the week's first fetched day
 *   income    = Σ incomeTotalPln
 *   expense   = Σ expenseTotalPln
 *   available = opening + income          («Располагаем»)
 *   end       = endBalancePln of the week's last fetched day («Останется»)
 *   minBalance= min endBalancePln across the week's days
 *   shortfall = minBalance < 0 (an intra-week dip counts even when the week
 *               ends positive)
 *
 * No React / moment-locale side effects beyond date math — unit-testable in
 * isolation (see `__tests__/weeklyModel.test.ts`).
 */
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment';

/** The minimal day shape the model needs (structural subset of the generated
 *  `PaymentCalendarDayType`, so both the calendar page and the dashboard can
 *  feed it query data without importing page-level types). */
export interface ILiquidityDayInput {
  date?: string | null;
  openingBalancePln?: number | null;
  incomeTotalPln?: number | null;
  expenseTotalPln?: number | null;
  endBalancePln?: number | null;
}

/** One aggregated ISO week of the liquidity forecast. */
export interface IWeekBucket {
  /** ISO Monday of the week ('YYYY-MM-DD'). */
  weekStart: string;
  /** ISO Sunday of the week ('YYYY-MM-DD'). */
  weekEnd: string;
  opening: number;
  income: number;
  expense: number;
  /** opening + income — what the account disposes of during the week. */
  available: number;
  /** Balance at the end of the week's last fetched day. */
  end: number;
  /** The worst end-of-day balance inside the week. */
  minBalance: number;
  /** True when the week dips below zero at any point. */
  shortfall: boolean;
}

const DATE_FMT = 'YYYY-MM-DD';

/** ISO Monday of the week containing `date`. */
export const isoMonday = (date: string): string =>
  moment(date).startOf('isoWeek').format(DATE_FMT);

/**
 * Aggregate day buckets into ascending ISO-week buckets. Days lacking a date
 * are skipped; weeks are keyed by their ISO Monday and each week aggregates
 * only the days actually present in the input (a partially fetched edge week
 * still produces a bucket).
 */
export const buildWeeklyModel = (
  days: ReadonlyArray<ILiquidityDayInput | null> | null | undefined,
): IWeekBucket[] => {
  const dated = (days || [])
    .filter((d): d is ILiquidityDayInput & { date: string } => !!d && !!d.date)
    .sort((a, b) => a.date.localeCompare(b.date));

  const byWeek = new Map<string, (ILiquidityDayInput & { date: string })[]>();
  dated.forEach((day) => {
    const monday = isoMonday(day.date);
    const bucket = byWeek.get(monday);
    if (bucket) bucket.push(day);
    else byWeek.set(monday, [day]);
  });

  return Array.from(byWeek.keys())
    .sort()
    .map((weekStart) => {
      const weekDays = byWeek.get(weekStart) as (ILiquidityDayInput & {
        date: string;
      })[];
      const first = weekDays[0];
      const last = weekDays[weekDays.length - 1];
      const opening = first.openingBalancePln || 0;
      const income = weekDays.reduce(
        (sum, d) => sum + (d.incomeTotalPln || 0),
        0,
      );
      const expense = weekDays.reduce(
        (sum, d) => sum + (d.expenseTotalPln || 0),
        0,
      );
      const ends = weekDays.map((d) => d.endBalancePln || 0);
      const minBalance = Math.min(...ends);
      return {
        weekStart,
        weekEnd: moment(weekStart).add(6, 'day').format(DATE_FMT),
        opening,
        income,
        expense,
        available: opening + income,
        end: last.endBalancePln || 0,
        minBalance,
        shortfall: minBalance < 0,
      };
    });
};
